import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';

// item 258 — the KPI strip stays pinned (sticky), but its EXPANDED bento must live in
// normal page flow BELOW the sticky stack, so the page pushes down and scrolls as one
// document instead of sliding under a pinned, semi-transparent panel. The strip (inside
// the sticky container) and the bento slot (below it) are separate places in the tree,
// so the open/peek state is shared here rather than held inside KpiStrip.
interface DashboardExpandValue {
  open: boolean;
  toggle: () => void;
  setOpen: (v: boolean) => void;
}

const DashboardExpandContext = createContext<DashboardExpandValue>({
  open: false,
  toggle: () => {},
  setOpen: () => {},
});

export const DashboardExpandProvider: React.FC = ({ children }) => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // the expanded bento is an on-demand PEEK, not a persistent panel — collapse it on
  // navigation so each page's own content stays the focus (item 96).
  useEffect(() => { setOpen(false); }, [pathname]);

  const value = useMemo<DashboardExpandValue>(
    () => ({ open, toggle: () => setOpen((p) => !p), setOpen }),
    [open],
  );

  return (
    <DashboardExpandContext.Provider value={value}>
      {children}
    </DashboardExpandContext.Provider>
  );
};

export const useDashboardExpand = (): DashboardExpandValue => useContext(DashboardExpandContext);

export default DashboardExpandContext;
